import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import api from "../../api/axios";
import ProductReviews from "../../pages/ProductReviews";

const RatingsReviews = ({
  productId,
  productVendorId,
  canReview,
  onWriteReview,
  onEditReview,
  initialSummary,
}) => {
  const [summary, setSummary] = useState(initialSummary || null);
  const [myReviewId, setMyReviewId] = useState(null);
  const [highlight, setHighlight] = useState(false);
  const reviewsRef = useRef(null);
  const location = useLocation();

  const handleGoToReviews = () => {
    reviewsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    if (location.hash === "#reviews") {
      setTimeout(() => {
        reviewsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location]);

  /* Load rating summary - only if initialSummary not provided */
  useEffect(() => {
    if (initialSummary) {
      // Use the provided summary, but still set it in state
      setSummary(initialSummary);
    } else {
      api
        .get(`/reviews/summary/${productId}`)
        .then((res) => setSummary(res.data))
        .catch(() => setSummary(null));
    }
  }, [productId, initialSummary]);

  /* Check if user already reviewed */
  useEffect(() => {
    if (!canReview) return;

    api
      .get(`/reviews/my/${productId}`)
      .then((res) => setMyReviewId(res.data.reviewId))
      .catch(() => setMyReviewId(null));
  }, [productId, canReview]);

  return (
    <Box
      ref={reviewsRef}
      sx={{
        transition: "0.3s",
        bgcolor: highlight ? "#fff9c4" : "transparent",
      }}
    >
      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography fontWeight={700}>Customer Reviews</Typography>

          {canReview && (
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: "none", fontWeight: 600 }}
              onClick={() =>
                myReviewId ? onEditReview(myReviewId) : onWriteReview()
              }
            >
              {myReviewId ? "Edit your review" : "Write a review"}
            </Button>
          )}
        </Box>

        {summary ? (
          <>
            {/* Average */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontSize={32} fontWeight={700}>
                {summary.average.toFixed(1)}
              </Typography>
              <Typography fontSize={20}>⭐</Typography>
            </Stack>

            {/* RATINGS SUMMARY */}
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                reviewsRef.current?.scrollIntoView({
                  behavior: "smooth",
                });
              }}
            >
              Based on {(summary.total || 0).toLocaleString()} ratings
            </Typography>

            {/* Distribution */}
            <Stack spacing={0.8} sx={{ mt: 2 }}>
              {[5, 4, 3, 2, 1].map((star) => {
                const percent = summary.distribution[star] || 0;
                return (
                  <Stack
                    key={star}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography variant="caption" sx={{ width: 42 }}>
                      {star} ★
                    </Typography>

                    <Box
                      sx={{
                        flex: 1,
                        height: 8,
                        bgcolor: "#eee",
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: `${percent}%`,
                          height: "100%",
                          bgcolor: "#ffa41c",
                        }}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ width: 36 }}>
                      {percent}%
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              Only verified purchases can leave reviews
            </Typography>

            {/* FULL REVIEWS SECTION */}
            <Box ref={reviewsRef}>
              <ProductReviews productId={productId} productVendorId={productVendorId} />
            </Box>
          </>
        ) : (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No ratings yet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Be the first to review this product
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RatingsReviews;